import { useSandboxRuntimes } from '../../hooks/use-sandbox-runtimes';
import { formatDuration, relativeTime } from '../skills/skill-invocation-helpers';

import { RunStatusBadge } from './run-status';

import type { SandboxRuntimeResponse } from '../../lib/api-client';
import type { OpenTabFn } from '../../shell/types';

function Th({ children, first = false }: { children: React.ReactNode; first?: boolean }) {
  return (
    <th
      style={{
        padding: first ? '8px 12px 8px 20px' : '8px 12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  first = false,
  mono = false,
}: {
  children: React.ReactNode;
  first?: boolean;
  mono?: boolean;
}) {
  return (
    <td
      style={{
        padding: first ? '9px 12px 9px 20px' : '9px 12px',
        verticalAlign: 'top',
        whiteSpace: 'nowrap',
        maxWidth: 260,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
      }}
    >
      {children}
    </td>
  );
}

/** Primary + soft secondary line inside one cell. */
function TwoLine({ primary, secondary }: { primary: React.ReactNode; secondary?: string | null }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{primary}</div>
      {secondary ? (
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-mute)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {secondary}
        </div>
      ) : null}
    </div>
  );
}

function Row({ row, onOpen }: { row: SandboxRuntimeResponse; onOpen: () => void }) {
  return (
    <tr
      onClick={onOpen}
      style={{
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        color: 'var(--text)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--bg-alt)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <Td first>
        <TwoLine
          primary={row.last_activity_at ? relativeTime(row.last_activity_at) : '—'}
          secondary={row.first_started_at ? `started ${relativeTime(row.first_started_at)}` : null}
        />
      </Td>
      <Td>{formatDuration(row.duration_ms)}</Td>
      <Td mono>
        <TwoLine primary={row.tenant_external_id ?? row.tenant_id} secondary={row.tenant_id} />
      </Td>
      <Td mono>
        <TwoLine
          primary={row.user_external_id ?? row.user_email ?? row.user_id ?? '—'}
          secondary={row.user_external_id ? (row.user_email ?? row.user_id) : row.user_id}
        />
      </Td>
      <Td mono>
        <TwoLine primary={row.pod_session_id} secondary={row.sandbox_name} />
      </Td>
      <Td>{row.runs_count}</Td>
      <Td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {row.live ? (
            <span
              title="Live — holds a warm sandbox lease"
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--kind-success, #2da44e)',
                animation: 'sd-pulse 1.6s ease-in-out infinite',
                flexShrink: 0,
              }}
            />
          ) : null}
          {row.last_status ? <RunStatusBadge status={row.last_status} /> : <span>—</span>}
        </span>
      </Td>
    </tr>
  );
}

/**
 * Sandbox Runtime Logs: every sandbox session that ran recently — live ones
 * pulsing, dead ones kept forever as history (kube-dashboard mental model).
 * Server-side search + cursor paging; row click opens the session detail
 * tab (header + Logs / Details / Traces).
 */
export function SandboxRuntimesView({ openTab }: { openTab?: OpenTabFn }) {
  const { rows, status, error, stale, search, setSearch, hasMore, loadingMore, loadMore, reload } =
    useSandboxRuntimes();

  const openDetail = (row: SandboxRuntimeResponse) => {
    openTab?.(
      'sandbox-runtime-detail',
      // tenantId rides along so the detail tab acts-as the row's (possibly
      // descendant) tenant — child-tenant sessions come from scope=subtree.
      { podSessionId: row.pod_session_id, tenantId: row.tenant_id },
      row.pod_session_id.slice(0, 24),
      { ephemeral: false },
    );
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: 'var(--bg)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 20px 4px',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
          Sandbox Runtime Logs
        </div>
        <button
          type="button"
          onClick={reload}
          style={{
            marginLeft: 'auto',
            padding: '4px 10px',
            fontSize: 11,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface)',
            color: 'var(--text)',
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-soft)', padding: '4px 20px 10px' }}>
        Every sandbox session, newest activity first. Live sessions pulse; finished ones stay as
        history. Click a row for logs, details, and traces.
      </div>
      <div style={{ padding: '0 20px 12px' }}>
        <input
          placeholder="Search session id, sandbox pod, claim, user external id / email, conversation external id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            maxWidth: 480,
            boxSizing: 'border-box',
            padding: '6px 10px',
            fontSize: 12,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface)',
            color: 'var(--text)',
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
      </div>

      {status === 'loading' && (
        <div style={{ padding: 20, fontSize: 12, color: 'var(--text-mute)' }}>Loading…</div>
      )}
      {status === 'error' && (
        <div style={{ padding: 20, fontSize: 12, color: 'var(--kind-failure)' }}>
          {error ?? 'Failed to load sandbox runtimes.'}
        </div>
      )}
      {status === 'success' && rows.length === 0 && (
        <div style={{ padding: 40, fontSize: 12, color: 'var(--text-mute)', textAlign: 'center' }}>
          {search
            ? 'No sessions match that search.'
            : 'No sandbox sessions yet — they appear here the moment one runs.'}
        </div>
      )}

      {rows.length > 0 && (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr
              style={{
                background: 'var(--bg-alt)',
                textAlign: 'left',
                color: 'var(--text-soft)',
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              <Th first>Time</Th>
              <Th>Duration</Th>
              <Th>Tenant</Th>
              <Th>User</Th>
              <Th>Session</Th>
              <Th>Runs</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <Row key={row.pod_session_id} row={row} onOpen={() => openDetail(row)} />
            ))}
          </tbody>
        </table>
      )}

      {hasMore && (
        <div style={{ padding: '12px 20px 24px' }}>
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              padding: '5px 14px',
              fontSize: 11,
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              background: 'var(--surface)',
              color: 'var(--text)',
              cursor: loadingMore ? 'default' : 'pointer',
              opacity: loadingMore ? 0.6 : 1,
            }}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
      {stale ? (
        <div
          title={error}
          style={{ padding: '0 20px 16px', fontSize: 11, color: 'var(--text-mute)' }}
        >
          Connection lost — retrying…
        </div>
      ) : null}
    </div>
  );
}
